import * as React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Skeleton } from './skeleton';

/**
 * LOADING STATE COMPONENTS
 *
 * Usage Guidelines:
 * - LoadingSpinner: Modals, small sections, buttons, quick operations
 * - TableSkeleton: List pages with tables
 * - PageSkeleton: Full page loading (header + content area)
 * - Skeleton: Building block for custom layouts (from skeleton.tsx)
 */

// =============================================================================
// LOADING SPINNER - Use for modals, small sections, quick operations
// =============================================================================

interface LoadingSpinnerProps {
  /** Optional text to display below spinner */
  text?: string;
  /** Size of the spinner */
  size?: 'sm' | 'md' | 'lg';
  /** Additional classes for the container */
  className?: string;
}

const spinnerSizes = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
};

export function LoadingSpinner({ text, size = 'md', className }: LoadingSpinnerProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center py-8', className)}>
      <Loader2 className={cn('animate-spin text-muted-foreground', spinnerSizes[size])} />
      {text && (
        <span className="mt-2 text-sm text-muted-foreground">{text}</span>
      )}
    </div>
  );
}

// =============================================================================
// TABLE SKELETON - Use for list pages with tables
// =============================================================================

interface TableSkeletonProps {
  /** Number of rows to display */
  rows?: number;
  /** Number of columns to display */
  columns?: number;
  /** Whether to show the header skeleton */
  showHeader?: boolean;
  /** Additional classes */
  className?: string;
}

export function TableSkeleton({
  rows = 5,
  columns = 5,
  showHeader = true,
  className,
}: TableSkeletonProps) {
  // Generate consistent widths for each column (seeded by column index)
  const columnWidths = React.useMemo(() =>
    Array.from({ length: columns }).map((_, i) => {
      const widths = [80, 120, 100, 60, 90, 110, 70, 130];
      return widths[i % widths.length];
    }), [columns]
  );

  return (
    <div className={cn('w-full', className)}>
      {showHeader && (
        <div className="flex gap-4 px-4 py-3 border-b">
          {columnWidths.map((width, i) => (
            <Skeleton key={`header-${i}`} className="h-4" style={{ width }} />
          ))}
        </div>
      )}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={`row-${rowIndex}`} className="flex gap-4 px-4 py-3 border-b last:border-b-0">
          {columnWidths.map((width, colIndex) => (
            <Skeleton
              key={`cell-${rowIndex}-${colIndex}`}
              className="h-4"
              style={{ width: width * (0.7 + (rowIndex * colIndex % 3) * 0.15) }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// PAGE SKELETON - Use for full page loading states
// =============================================================================

interface PageSkeletonProps {
  /** Show header skeleton (title + subtitle + buttons) */
  showHeader?: boolean;
  /** Show filter bar skeleton */
  showFilters?: boolean;
  /** Content type */
  variant?: 'table' | 'cards' | 'detail';
  /** Number of table rows or cards */
  itemCount?: number;
  /** Additional classes */
  className?: string;
}

export function PageSkeleton({
  showHeader = true,
  showFilters = true,
  variant = 'table',
  itemCount = 8,
  className,
}: PageSkeletonProps) {
  return (
    <div className={cn('px-6 py-4 space-y-4', className)}>
      {/* Header skeleton */}
      {showHeader && (
        <div className="flex items-end justify-between">
          <div>
            <Skeleton className="h-7 w-48 mb-2" />
            <Skeleton className="h-4 w-72" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-20" />
            <Skeleton className="h-8 w-24" />
          </div>
        </div>
      )}

      {/* Filter bar skeleton */}
      {showFilters && (
        <div className="rounded-lg border bg-card p-4">
          <div className="flex gap-4">
            <Skeleton className="h-9 flex-1 max-w-sm" />
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-9 w-32" />
          </div>
        </div>
      )}

      {/* Content skeleton */}
      {variant === 'table' && (
        <div className="rounded-lg border bg-card">
          <TableSkeleton rows={itemCount} columns={6} />
        </div>
      )}

      {variant === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: itemCount }).map((_, i) => (
            <div key={i} className="rounded-lg border bg-card p-4">
              <Skeleton className="h-5 w-3/4 mb-3" />
              <Skeleton className="h-4 w-1/2 mb-2" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      )}

      {variant === 'detail' && (
        <div className="space-y-4">
          <div className="rounded-lg border bg-card p-6">
            <Skeleton className="h-6 w-1/3 mb-4" />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-5 w-32" />
              </div>
              <div>
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-5 w-28" />
              </div>
              <div>
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-5 w-36" />
              </div>
              <div>
                <Skeleton className="h-4 w-20 mb-2" />
                <Skeleton className="h-5 w-24" />
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-card p-6">
            <Skeleton className="h-5 w-24 mb-4" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-4/5" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
